import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import client from "../../../shared/api/client";
import DestructiveConfirmModal from "../../../shared/DestructiveConfirmModal";

const initials = (name) =>
  (name || "").trim().split(/\s+/).slice(0, 2).map((word) => word[0]).join("").toUpperCase();

const EMPTY_FORM = { name: "", agency: "", email: "", phone: "" };

const ArtistContacts = ({ contacts: initialContacts, addContactUrl, removeContactUrl, agencySearchUrl, agentSearchUrl, canManage, canRemove, rostrLogoUrl, rostrContactUrl }) => {
  const [contacts, setContacts] = useState(initialContacts || []);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [agencySuggestions, setAgencySuggestions] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const agencyDebounce = useRef(null);
  const nameDebounce = useRef(null);

  const [revealed, setRevealed] = useState({});

  const setReveal = (id, value) => setRevealed((prev) => ({ ...prev, [id]: value }));

  const reveal = (contact) => {
    if (!rostrContactUrl || revealed[contact.id] === "loading") return;
    setReveal(contact.id, "loading");
    client.post(rostrContactUrl, { person_uuid: contact.person_uuid })
      .then((res) => {
        const entries = (res.data && res.data.entries) || [];
        if (!entries.length) { setReveal(contact.id, "No contact on file at ROSTR."); return; }
        const primary = entries.find((e) => e.email) || entries[0];
        const company = primary.company && primary.company.name;
        setContacts((list) => list.map((c) => (c.id === contact.id
          ? { ...c, email: primary.email || c.email, company: company || c.company }
          : c)));
        setReveal(contact.id, entries);
      })
      .catch((e) => {
        const status = e && e.response && e.response.status;
        const messages = {
          403: "Connect an active ROSTR Pro account to see contact details.",
          404: "ROSTR has no record for this person.",
        };
        setReveal(contact.id, messages[status] || "Could not load contact — please try again.");
      });
  };

  const autoRevealed = useRef(new Set());
  useEffect(() => {
    if (!canManage || !rostrContactUrl) return;
    contacts.forEach((contact) => {
      if (contact.source === "rostr" && contact.contactable && !autoRevealed.current.has(contact.id)) {
        autoRevealed.current.add(contact.id);
        reveal(contact);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, canManage, rostrContactUrl]);

  useEffect(() => {
    const onUpdate = (event) => {
      if (Array.isArray(event.detail)) setContacts(event.detail);
    };
    window.addEventListener("artist:contacts-updated", onUpdate);
    return () => window.removeEventListener("artist:contacts-updated", onUpdate);
  }, []);

  const reset = () => { setForm(EMPTY_FORM); setError(null); setAgencySuggestions([]); setNameSuggestions([]); };
  const openForm = () => { reset(); setOpen(true); };
  const closeForm = () => { setOpen(false); reset(); };

  const canSave = form.name.trim() && form.email.trim() && form.agency.trim();

  const onNameChange = (value) => {
    setForm((prev) => ({ ...prev, name: value }));
    if (nameDebounce.current) clearTimeout(nameDebounce.current);
    if (!value.trim() || !agentSearchUrl) { setNameSuggestions([]); return; }
    nameDebounce.current = setTimeout(() => {
      client.get(agentSearchUrl, { params: { query: value } })
        .then((res) => setNameSuggestions(res.data || []))
        .catch(() => setNameSuggestions([]));
    }, 250);
  };

  const pickAgent = (agent) => {
    if (nameDebounce.current) clearTimeout(nameDebounce.current);
    setForm({
      name: agent.name || "",
      agency: agent.agency || "",
      email: agent.email || "",
      phone: agent.phone || "",
    });
    setNameSuggestions([]);
  };

  const onAgencyChange = (value) => {
    setForm((prev) => ({ ...prev, agency: value }));
    if (agencyDebounce.current) clearTimeout(agencyDebounce.current);
    if (!value.trim() || !agencySearchUrl) { setAgencySuggestions([]); return; }
    agencyDebounce.current = setTimeout(() => {
      client.get(agencySearchUrl, { params: { name: value } })
        .then((res) => setAgencySuggestions(res.data || []))
        .catch(() => setAgencySuggestions([]));
    }, 250);
  };

  const pickAgency = (agency) => {
    if (agencyDebounce.current) clearTimeout(agencyDebounce.current);
    setForm((prev) => ({ ...prev, agency: agency.name }));
    setAgencySuggestions([]);
  };

  const save = () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError(null);
    client.post(addContactUrl, { name: form.name, email: form.email, agency_name: form.agency, phone: form.phone })
      .then((res) => {
        setContacts((prev) => [...prev, res.data]);
        setOpen(false);
        reset();
        setSaving(false);
      })
      .catch((e) => {
        setSaving(false);
        const data = e && e.response && e.response.data;
        setError((data && data[0]) || "Could not add this contact.");
      });
  };

  const remove = (contact) => {
    setError(null);
    setContacts((list) => list.filter((c) => c.id !== contact.id));
    client.delete(removeContactUrl, { data: { agent_id: contact.id } })
      .catch(() => {
        setContacts((list) => list.some((c) => c.id === contact.id) ? list : [...list, contact]);
        setError("Could not remove this contact.");
      });
  };

  const visible = showAll ? contacts : contacts.slice(0, 3);
  const remaining = contacts.length - 3;

  const contactForm = (
    <div className="artist-contact-form" data-testid="add-contact-form">
      <div className="setlist-field">
        <label className="setlist-label">Full name</label>
        <input
          type="text"
          className="setlist-input"
          placeholder="Full name"
          value={form.name}
          autoFocus
          autoComplete="off"
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={() => setTimeout(() => setNameSuggestions([]), 150)}
          data-testid="contact-name"
        />
        {nameSuggestions.length > 0 && (
          <div className="agency-suggestions" data-testid="name-suggestions">
            {nameSuggestions.map((agent) => (
              <div
                key={agent.id}
                className="agency-suggestion"
                data-testid="name-suggestion"
                onMouseDown={() => pickAgent(agent)}
              >
                {agent.name}{agent.agency ? ` · ${agent.agency}` : ""}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="setlist-field agency-typeahead">
        <label className="setlist-label">Agency</label>
        <input
          type="text"
          className="setlist-input"
          placeholder="Search or add an agency"
          value={form.agency}
          autoComplete="off"
          onChange={(e) => onAgencyChange(e.target.value)}
          onBlur={() => setTimeout(() => setAgencySuggestions([]), 150)}
          data-testid="contact-agency"
        />
        {agencySuggestions.length > 0 && (
          <div className="agency-suggestions" data-testid="agency-suggestions">
            {agencySuggestions.map((agency) => (
              <div
                key={agency.id}
                className="agency-suggestion"
                data-testid="agency-suggestion"
                onMouseDown={() => pickAgency(agency)}
              >
                {agency.name}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="setlist-field">
        <label className="setlist-label">Email</label>
        <input
          type="email"
          className="setlist-input"
          placeholder="name@example.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="contact-email"
        />
      </div>
      <div className="setlist-field">
        <label className="setlist-label">Phone <span className="setlist-optional">(optional)</span></label>
        <input
          type="tel"
          className="setlist-input"
          placeholder="(555) 555-5555"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          data-testid="contact-phone"
        />
      </div>
      {error && <div className="setlist-error" data-testid="contact-error">{error}</div>}
      <div className="d-flex justify-content-end mt-2">
        <button type="button" className="btn btn-link btn-sm mr-2" onClick={closeForm}>Cancel</button>
        <button type="button" className="btn btn-primary btn-sm" disabled={!canSave || saving} onClick={save} data-testid="contact-save">
          {saving ? "Adding…" : "Add contact"}
        </button>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0 font-weight-bold">
          Contacts <span className="social-dash-count">({contacts.length})</span>
        </h5>
        {canManage && !open && (
          <button type="button" className="dash-card-action" onClick={openForm} title="Add a contact" aria-label="Add a contact" data-testid="add-contact-btn">
            <i className="fa fa-plus" />
          </button>
        )}
      </div>

      {open && contactForm}

      {!open && error && <div className="setlist-error mb-2" data-testid="contact-list-error">{error}</div>}

      {contacts.length === 0 && !open ? (
        <div className="dash-empty-state text-center">
          <span className="dash-empty-icon"><i className="fa fa-user-plus" /></span>
          <h6 className="dash-empty-title mt-3 mb-1">No contacts yet</h6>
          <p className="dash-empty-desc mb-3">Add the agent or manager you work with for this artist.</p>
          {canManage && (
            <button type="button" className="btn btn-primary btn-sm dash-empty-action" onClick={openForm}>Add a contact</button>
          )}
        </div>
      ) : (
        <React.Fragment>
          {visible.map((contact) => {
            const isRostr = contact.source === "rostr";
            const details = revealed[contact.id];
            return (
            <div className="contact-row" key={contact.id}>
              <div className="d-flex align-items-center">
                <span className="contact-avatar">{initials(contact.name)}</span>
                <div className="contact-main ml-2 d-flex flex-column">
                  {contact.path ? (
                    <a className="contact-name" href={contact.path} {...(isRostr ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{contact.name}</a>
                  ) : (
                    <span className="contact-name">{contact.name}</span>
                  )}
                  <span className="contact-role">
                    {contact.role}
                    {contact.company && " · "}
                    {contact.company && (contact.company_path ? (
                      <a href={contact.company_path}>{contact.company}</a>
                    ) : contact.company)}
                    {isRostr && (rostrLogoUrl
                      ? <img className="contact-rostr-badge ml-1" src={rostrLogoUrl} alt="ROSTR" title="Sourced from ROSTR" />
                      : <span className="contact-rostr-badge ml-1">ROSTR</span>)}
                  </span>
                </div>
                <div className="contact-actions ml-auto d-flex align-items-center">
                  {details === "loading" && <span className="rostr-contact-btn">Loading…</span>}
                  {contact.email && (
                    <a className="contact-email" href={`mailto:${contact.email}`} title={`Email ${contact.name}`}>
                      <i className="far fa-envelope" />
                    </a>
                  )}
                  {canRemove && !isRostr && (
                    <DestructiveConfirmModal
                      trigger={
                        <button type="button" className="contact-remove btn btn-link p-0 ml-2" title={`Remove ${contact.name}`} aria-label={`Remove ${contact.name}`} data-testid="contact-remove">
                          <i className="far fa-trash-alt" />
                        </button>
                      }
                      title="Remove contact?"
                      body={`Remove ${contact.name} as a contact for this artist? This can’t be undone, but their contact record itself is kept.`}
                      confirmLabel="Remove"
                      onConfirm={() => remove(contact)}
                    />
                  )}
              </div>
              </div>
              {Array.isArray(details) ? (
                <div className="rostr-contact-details" data-testid="rostr-contact-details">
                  {details.map((entry, i) => (
                    <div className="rostr-contact-line" key={i}>
                      {entry.company && entry.company.name && (
                        <span className="rostr-contact-company">{entry.company.name}</span>
                      )}
                      {entry.email && <a href={`mailto:${entry.email}`}>{entry.email}</a>}
                      {(entry.phone || entry.office_phone) && (
                        <span className="rostr-contact-phone">{entry.phone || entry.office_phone}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (typeof details === "string" && details !== "loading" && (
                <div className="rostr-contact-details" data-testid="rostr-contact-details">{details}</div>
              ))}
            </div>
            );
          })}
          {!showAll && remaining > 0 && (
            <button type="button" className="btn btn-link p-0 social-dash-link" onClick={() => setShowAll(true)} data-testid="show-more-contacts">
              Show {remaining} more contact{remaining === 1 ? "" : "s"}
            </button>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

ArtistContacts.propTypes = {
  contacts: PropTypes.array,
  addContactUrl: PropTypes.string.isRequired,
  removeContactUrl: PropTypes.string,
  agencySearchUrl: PropTypes.string,
  agentSearchUrl: PropTypes.string,
  canManage: PropTypes.bool,
  canRemove: PropTypes.bool,
  rostrLogoUrl: PropTypes.string,
  rostrContactUrl: PropTypes.string,
};

ArtistContacts.defaultProps = { contacts: [], removeContactUrl: null, agencySearchUrl: null, agentSearchUrl: null, canManage: false, canRemove: false, rostrLogoUrl: null, rostrContactUrl: null };

export default ArtistContacts;
